import { ActionSheetProvider } from '@expo/react-native-action-sheet';
import { AdminLoginAlert } from '@psl/appComponents/AdminLoginAlert';
import { RoutesContainer } from '@psl/appComponents/navigation/RoutesContainer';
import { AppRoot } from '@psl/appComponents/seo/AppRoot';
import {
  FontConfigProvider,
  kDefaultFontConfig,
} from '@psl/components/typography/config';
import Routes from '@psl/flows/Routes';
import { ContinuousOnboardingPresenterProvider } from '@psl/flows/seller-onboarding/components/ContinuousOnboardingPresenterProvider';
import { SellerOnboardingInitializer } from '@psl/flows/seller-onboarding/initializers';
import * as Device from 'expo-device';
import { DeviceType } from 'expo-device';
import * as ScreenOrientation from 'expo-screen-orientation';
import { lazy, memo, Suspense, useEffect } from 'react';
import { Platform } from 'react-native';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { Slot, Provider as SlotFillProvider } from 'react-slot-fill';
import { ThemeProvider } from 'styled-components';
import { SWRConfig } from 'swr';
import { AppContextProvider } from './appComponents/AppContext';
import { AudioController } from './appComponents/audioController/AudioContext';
import { CommonAPIProvider } from './appComponents/commonAPI/CommonAPIProvider';
import { EnsureAppReady } from './appComponents/EnsureAppReady';
import { AppLayoutProvider } from './appComponents/layout/AppLayoutContext';
import { MediaPlaybackProvider } from './appComponents/media/MediaPlaybackContext';
import { ModalPresentationContextProvider } from './appComponents/ModalWrapper';
import { GlobalSearchContextProvider } from './appComponents/search/GlobalSearchContext';
import {
  UserInterfaceIdiomProvider,
  UserInterfaceStyle,
} from './appComponents/UserInterfaceIdiomContext';
import { useTrackVersionUpgrade } from './appComponents/VersionText';
import { WiredStatsigProvider } from './appComponents/WiredStatsigProvider';
import { ButtonVariant } from './components/button/Button';
import { DefaultButtonVariantProvider } from './components/button/ButtonFramework/Button';
import ErrorFallback from './components/error/ErrorFallback';
import { HoldItemProvider } from './components/holdMenu/HoldMenu';
import { useKeyboardManagerMonitor } from './components/KeyboardManager';
import { DefaultLoadingIndicator } from './components/LoadingIndicator/DefaultLoadingIndicator';
import { LoadingIndicatorProvider } from './components/LoadingIndicator/LoadingIndicator';
import { ImageViewerContextProvider } from './components/media/ImageViewerContext';
import { AppModalProvider } from './components/modal/AppModal';
import { WiredSnackbar } from './components/snackbar/Snackbar';
import { ChatGlobalContextProvider } from './flows/community/chat/context/GlobalChatContext';
import { DiscoveryGlobalProvider } from './flows/discovery/components/DiscoveryGlobalProvider';
import { WiredAutoDismissIntroVideo } from './flows/onboarding/components/WiredAutoDismissIntroVideo';
import { SellerOnboardingContextProvider } from './flows/seller-onboarding/Provider';
import { GlobalShoppingContextProvider } from './flows/shop/provider';
import { OnLineStatusTracking } from './flows/showcase/components/OnLineStatusTracking';
import { useStatsig } from './functions/abtest/useStatsig';
import { setAnalyticsUser } from './functions/analytics';
import analyticsHelper from './functions/analytics/analyticsHelper';
import { launchBegin, LaunchTrackFinish } from './functions/appLaunchTracking';
import { RefreshUserTokenOnUserTypeChange } from './functions/auth/useUpdateAuthTokenOnUserTypeChange';
import { currentUserId$ } from './functions/auth/utils';
import {
  setCrashUser,
  TouchEventBoundary,
  withErrorBoundary,
} from './functions/debugging/crashTracking';
import { useCheckForUpdates } from './functions/hooks/useCheckForUpdates';
import { NotificationHandlers } from './functions/notification/NotificationHandlers';
import { ResponsiveContextProvider } from './functions/responsive/ResponsiveContext';
import { ServiceFactoryProvider } from './functions/services';
import { useTrackSession } from './functions/user/sessionTracker';
import { theme } from './themes/Default';
import { safeMarkDevFlag } from './utils/safeMarkDevFlag';

const kUseAppLevelErrorBoundaryInDev = safeMarkDevFlag(false);

const ContinuousOnboardingPresenterWithErrorBoundary = lazy(
  () =>
    import(
      '@psl/flows/seller-onboarding/components/WiredContinuousOnboardingPresenter'
    ),
);

const WiredChatNotificationSummary = lazy(
  () =>
    import('./flows/community/chat/component/common/ChatNotificationSummary'),
);

// have to prevent splash screen being auto hidden early before app renders
launchBegin({ preventSplashScreenAutoHide: true });

const AppHooks = memo(() => {
  useCheckForUpdates();
  useKeyboardManagerMonitor();
  useStatsig();
  useEffect(() => {
    analyticsHelper.trackApplicationLaunch().catch(() => null);
    const sub = currentUserId$.subscribe({
      next: uid => {
        setCrashUser(uid);
        setAnalyticsUser(uid);
      },
    });
    return () => sub.unsubscribe();
  }, []);
  useTrackSession();
  useTrackVersionUpgrade();
  useEffect(() => {
    (async () => {
      if (
        Platform.OS !== 'web' &&
        (await Device.getDeviceTypeAsync()) === DeviceType.PHONE
      ) {
        await ScreenOrientation.lockAsync(
          ScreenOrientation.OrientationLock.PORTRAIT_UP,
        );
      }
    })().catch(() => null);
  }, []);
  return null;
});

const App = memo(() => {
  // default to dark mode for all platforms
  const userInterfaceStyle: UserInterfaceStyle = 'dark';

  return (
    <SWRConfig>
      <AppRoot>
        <MediaPlaybackProvider>
          <AudioController>
            <ServiceFactoryProvider>
              <AppContextProvider>
                <ImageViewerContextProvider>
                  <ModalPresentationContextProvider>
                    <UserInterfaceIdiomProvider
                      name="global"
                      userInterfaceStyle={userInterfaceStyle}
                    >
                      <FontConfigProvider config={kDefaultFontConfig}>
                        <ResponsiveContextProvider>
                          <WiredStatsigProvider>
                            <ThemeProvider theme={theme}>
                              <DefaultButtonVariantProvider
                                ButtonVariant={ButtonVariant}
                              >
                                <Suspense fallback={null}>
                                  <AppHooks />
                                  <OnLineStatusTracking />
                                  <RefreshUserTokenOnUserTypeChange />
                                </Suspense>
                                <TouchEventBoundary>
                                  <Suspense fallback={null}>
                                    <EnsureAppReady>
                                      <SafeAreaProvider>
                                        <AppLayoutProvider>
                                          <GlobalSearchContextProvider>
                                            <ActionSheetProvider>
                                              <LoadingIndicatorProvider
                                                loadingIndicator={
                                                  <DefaultLoadingIndicator />
                                                }
                                              >
                                                <SlotFillProvider>
                                                  <HoldItemProvider>
                                                    <AppModalProvider>
                                                      <CommonAPIProvider>
                                                        <ChatGlobalContextProvider>
                                                          <SellerOnboardingContextProvider>
                                                            <DiscoveryGlobalProvider>
                                                              <GlobalShoppingContextProvider>
                                                                <ContinuousOnboardingPresenterProvider>
                                                                  <RoutesContainer>
                                                                    <Routes />
                                                                    <WiredSnackbar />
                                                                    <NotificationHandlers />
                                                                    <AdminLoginAlert />
                                                                    <WiredChatNotificationSummary />
                                                                    <LaunchTrackFinish />
                                                                    <SellerOnboardingInitializer />
                                                                    <ContinuousOnboardingPresenterWithErrorBoundary />
                                                                  </RoutesContainer>
                                                                </ContinuousOnboardingPresenterProvider>
                                                              </GlobalShoppingContextProvider>
                                                            </DiscoveryGlobalProvider>
                                                          </SellerOnboardingContextProvider>
                                                        </ChatGlobalContextProvider>
                                                      </CommonAPIProvider>
                                                    </AppModalProvider>
                                                  </HoldItemProvider>
                                                  <Slot name="portal" />
                                                  <Suspense fallback={null}>
                                                    <WiredAutoDismissIntroVideo />
                                                  </Suspense>
                                                </SlotFillProvider>
                                              </LoadingIndicatorProvider>
                                            </ActionSheetProvider>
                                          </GlobalSearchContextProvider>
                                        </AppLayoutProvider>
                                      </SafeAreaProvider>
                                    </EnsureAppReady>
                                  </Suspense>
                                </TouchEventBoundary>
                              </DefaultButtonVariantProvider>
                            </ThemeProvider>
                          </WiredStatsigProvider>
                        </ResponsiveContextProvider>
                      </FontConfigProvider>
                    </UserInterfaceIdiomProvider>
                  </ModalPresentationContextProvider>
                </ImageViewerContextProvider>
              </AppContextProvider>
            </ServiceFactoryProvider>
          </AudioController>
        </MediaPlaybackProvider>
      </AppRoot>
    </SWRConfig>
  );
});

export default __DEV__ && !kUseAppLevelErrorBoundaryInDev
  ? App
  : withErrorBoundary(App, { fallback: ErrorFallback });
